import {Component, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AlertingApiKey, AlertingApiKeys, ApiKey, ApiKeys} from '../../../models/apiKey';

import {MatDialog} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from 'src/app/components';
import {CopyTokenDialogComponent} from './copy-token-dialog/copy-token-dialog.component';
import {getDaysDifferenceFromNow, getDaysDifferenceToNow} from '../../../utilities/date';
import {sortAsc} from '../../../utilities/arrays';
import {SessionService} from '../../../services/session/session.service';
import {DataService, SnackbarService} from '../../../services';

@Component({
    selector: 'app-api-key',
    templateUrl: './api-key.component.html',
    styleUrls: ['./api-key.component.scss']
})
export class ApiKeyComponent implements OnInit{
    apiKeys: ApiKeys = [];

    newApiKey: FormGroup;

    expirationHelpText: string;
    inactivityMessage: string;
    inavtiveDayMax: number;
    minDate: Date;
    maxDate: Date;

    initializing = true;
    private isServiceAccount: boolean = false;

    error = false;

    constructor(public sessionService: SessionService, public dataService: DataService, private snackbarService: SnackbarService, public dialog: MatDialog, public fb: FormBuilder) {
    }

    ngOnInit() {
        this.newApiKey = this.fb.group({
            name: ['', Validators.required],
            expiration: ['']
        });

        this.dataService.getUserInfo().subscribe({
            next: res => {
                this.isServiceAccount = res['email'].startsWith('svc');
                this.minDate = new Date();
                this.minDate.setDate(this.minDate.getDate() + 1);

                this.setMessage();
                if (this.isServiceAccount) {
                    this.inactivityMessage = 'This token will automatically expire in 1 year of inactivity.';
                    this.inavtiveDayMax = 365;
                    this.maxDate = new Date();
                    this.maxDate.setFullYear(this.maxDate.getFullYear() + 10);
                    this.maxDate.setHours(0,0,0,0);
                } else {
                    this.inactivityMessage = 'This token will automatically expire in 4 months of inactivity.';
                    this.inavtiveDayMax = 120;
                    this.maxDate = new Date();
                    this.maxDate.setFullYear(this.maxDate.getFullYear() + 1);
                    this.maxDate.setHours(0,0,0,0);
                    const expInput = this.newApiKey.get('expiration');
                    expInput.setValue(this.maxDate);
                }

                this.getKeys();
            }, error: () => {
                this.snackbarService.openSnackBar('Please save your user profile in Alerting to generate a token.', true);
            }
        });
    }

    setMessage(): void {
        if(this.newApiKey.get('expiration').invalid) {
            this.expirationHelpText = "Invalid date | ";
            this.error = true;
        } else {
            this.expirationHelpText = "MM/DD/YYYY | ";
            this.error = false;
        }
        if (this.isServiceAccount) {
            this.expirationHelpText = this.expirationHelpText + 'Max of 10 years';
        } else {
            this.expirationHelpText = this.expirationHelpText + 'Max of 1 year';
        }
    }

    getKeys(): void {
        this.sessionService.getApiKeys().subscribe( (keys: AlertingApiKeys) => {
            this.apiKeys = sortAsc(keys.content, 'expires_at');
            this.initializing = false;
        });
    }

    revokeKey(key: ApiKey): void {
        this.dialog.open(ConfirmationDialogComponent, {
            width: '450px',
            data: {
                title:           `Delete API Key`,
                message:         `Are you sure you want to PERMANENTLY delete "${key.name}"?`,
                confirmBtnText:  'Revoke',
                confirmBtnColor: 'error'
            }
        }).afterClosed().subscribe((response) => {
            if (response) {
                this.sessionService.revokeApiKey(key.id).subscribe((keys: AlertingApiKey) => {
                    this.apiKeys = this.apiKeys.filter(k => k.id != key.id);
                });
            }
        });
    }

    createKey(): void {
        this.newApiKey.markAllAsTouched();

        if (this.newApiKey.valid) {
            const form = this.newApiKey.getRawValue();
            const request: ApiKey = {
                name: form.name,
                expires_at: form.expiration ? form.expiration.toISOString() : undefined
            };

            this.sessionService.createApiKey(request).subscribe((apiKey: AlertingApiKey) => {
                this.openCopyToken(apiKey.content);
            });
        }
    }

    openCopyToken(key: ApiKey): void {
        this.dialog.open(CopyTokenDialogComponent, {data: key, width: '450px',  disableClose: true}).afterClosed().subscribe( () => {
            this.newApiKey.reset({expiration: this.maxDate});
            this.getKeys();
        });
    }

    isExpirationRequired() {
        const expValidator = this.newApiKey.get('expiration').validator;
        return expValidator && expValidator({} as AbstractControl)?.required;
    }

    isAboutToExpire(key: ApiKey): boolean {
        return this.isAboutToAbsoluteExpire(key) || this.isAboutToInactiveExpire(key);
    }

    absoluteExpiredDayCount(key: ApiKey): number {
        return getDaysDifferenceFromNow(key.expires_at);
    }

    isAboutToAbsoluteExpire(key: ApiKey): boolean {
        return this.absoluteExpiredDayCount(key) <= 7;
    }

    inactiveDayCount(key: ApiKey): number {
        const lastUsedDate = key.last_used ? key.last_used : key.created_date;
        return this.inavtiveDayMax - getDaysDifferenceToNow(lastUsedDate);
    }

    isAboutToInactiveExpire(key: ApiKey): boolean {
        return this.inactiveDayCount(key) <= 7;
    }

    isExpired(key: ApiKey): boolean {
        return this.absoluteExpiredDayCount(key) < 0 || this.inactiveDayCount(key) < 0;
    }

    getKeyWarningMessage(key: ApiKey): string {
        switch (true) {
            case this.isExpired(key):
                return 'Key is expired.';
            case this.isAboutToAbsoluteExpire(key):
                const expDaysLeft = this.absoluteExpiredDayCount(key);
                return `Key will expire in ${expDaysLeft} day${expDaysLeft > 1 ? 's' : ''}.`;
            case this.isAboutToInactiveExpire(key):
                const inactiveDaysLeft = this.inactiveDayCount(key);
                return `Key will expire in ${inactiveDaysLeft} more inactive day${inactiveDaysLeft > 1 ? 's' : ''}.`;
        }
    }
}
